import React, { useState, useEffect, useRef } from 'react';
import { Box, Container, Grid, Typography, IconButton, Link } from '@mui/material';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import GoogleIcon from '@mui/icons-material/Google';
import InstagramIcon from '@mui/icons-material/Instagram';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import netBg from '../Media/net.png';

const SocialMediaLinks = () => {
  const [hoveredIcon, setHoveredIcon] = useState(null);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, amount: 0.2 });
  
  const socialIcons = [
    {
      icon: <GoogleIcon />,
      color: '#DB4437',
      name: 'Google',
      url: 'https://g.page/r/CTSDYFPS7L8YEAE/review',
      ariaLabel: 'Visit our Google Business Profile'
    },
    {
      icon: <LinkedInIcon />,
      color: '#0077B5',
      name: 'LinkedIn',
      url: 'https://www.linkedin.com/company/mortgageline-powered-by-dominion-lending-centres/?originalSubdomain=ca',
      ariaLabel: 'Follow us on LinkedIn'
    },
    {
      icon: <FacebookIcon />,
      color: '#1877F2',
      name: 'Facebook',
      url: 'https://www.facebook.com/mymortgageline/',
      ariaLabel: 'Follow us on Facebook'
    },
    {
      icon: <TwitterIcon />,
      color: '#1DA1F2',
      name: 'Twitter',
      url: 'https://x.com/mymortgageline',
      ariaLabel: 'Follow us on Twitter'
    },
    {
      icon: <InstagramIcon />,
      color: '#E4405F',
      name: 'Instagram',
      url: 'https://www.instagram.com/mymortgageline/',
      ariaLabel: 'Follow us on Instagram'
    }
  ];

  return (
    <Box
      ref={ref}
      component={motion.div}
      initial={{ y: -20, opacity: 0 }}
      animate={
        isInView 
          ? { y: 0, opacity: 1, transition: { duration: 0.5, staggerChildren: 0.1 } }
          : { y: -20, opacity: 0, transition: { duration: 0.3 } }
      }
      sx={{
        display: 'flex',
        justifyContent: 'center',
        gap: 1,
        py: 2,
        backgroundColor: (theme) => theme.palette.primary.main, 
        borderRadius: '8px',
        maxWidth: '350px',
        margin: 'auto',
        marginBottom: 3.5,
        position: 'relative',
        zIndex: 1,
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
        transform: 'translateY(50%)'
      }}
    >
      {socialIcons.map((socialItem, index) => (
  <Box 
    key={index} 
    component={motion.div}
    variants={{
      hidden: { y: -10, opacity: 0 },
      visible: { 
        y: 0, 
        opacity: 1,
        transition: { delay: index * 0.1 }
      }
    }}
    initial="hidden"
    animate={isInView ? "visible" : "hidden"}
    sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}
  >
    <IconButton
  component={motion.a}
  href={socialItem.url}
  target="_blank"
            rel="noopener noreferrer"
            aria-label={socialItem.ariaLabel}
  whileHover={{ 
    scale: 1.15, 
    rotate: [0, 10, -10, 0],
    transition: { duration: 0.3 } 
  }}
  whileTap={{ scale: 0.9 }}
  onMouseEnter={() => setHoveredIcon(index)}
  onMouseLeave={() => setHoveredIcon(null)}
  sx={{
    color: socialItem.color,
    backgroundColor: 'white',
              '&:hover': { 
                backgroundColor: '#f5f5f5',
                outline: 'none'
              },
              '&:focus': {
                outline: `2px solid ${socialItem.color}`,
                outlineOffset: '2px'
              },
              '&:focus-visible': {
                outline: `2px solid ${socialItem.color}`,
                outlineOffset: '2px'
              },
    position: 'relative',
    zIndex: 2
  }}
>
  {socialItem.icon}
</IconButton>
    
    <AnimatePresence>
      {hoveredIcon === index && (
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: -30 }}
          exit={{ opacity: 0, y: 10 }}
          transition={{ duration: 0.2 }}
          sx={{
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: 'rgba(0,0,0,0.7)',
            color: 'white',
            padding: '3px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            whiteSpace: 'nowrap',
            zIndex: 3,
            pointerEvents: 'none'
          }}
                role="tooltip"
                aria-hidden="true"
        >
          {socialItem.name}
        </Box>
      )}
    </AnimatePresence>
    
    <Box
      component={motion.div}
      animate={{
        boxShadow: hoveredIcon === index 
          ? `0 0 8px 2px ${socialItem.color}` 
          : '0 0 0px 0px transparent'
      }}
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        borderRadius: '50%',
        zIndex: 1
      }}
    />
  </Box>
))}
    </Box>
  );
};


const Footer = () => {
  const linkMap = {
    'Home': {
      url: '/',
      ariaLabel: 'Return to Mortgage Line homepage'
    },
    'Our Service': {
      url: '/Services',
      ariaLabel: 'View our mortgage services and solutions'
    },
    'Rates': {
      url: 'https://www.mymortgageline.ca/rates/',
      ariaLabel: 'Check current mortgage rates and offers'
    },
    'Contact': {
      url: '/Contact',
      ariaLabel: 'Contact Mortgage Line for assistance'
    },
    'Our Team': {
      url: 'https://www.mymortgageline.ca/our-associates/',
      ariaLabel: 'Meet our mortgage professionals and team members'
    },
    'Fact Find': {
      url: 'https://www.mymortgageline.ca/resources/',
      ariaLabel: 'Complete our mortgage fact finder questionnaire'
    },
    'Client Documentation': {
      url: 'https://www.mymortgageline.ca/resources/',
      ariaLabel: 'Access required client documentation and forms'
    },
    'Mortgage Calculators': {
      url: 'https://www.mymortgageline.ca/mortgage-payment-calculator/',
      ariaLabel: 'Calculate your mortgage payments and affordability'
    },
    'Make a Referal': {
      url: 'https://www.mymortgageline.ca/contact/',
      ariaLabel: 'Refer someone to Mortgage Line'
    },
    'Inquiry': {
      url: 'https://www.mymortgageline.ca/contact/',
      ariaLabel: 'Submit a mortgage inquiry or question'
    },
    'Copyright Statements': {
      url: 'https://www.mymortgageline.ca/privacy-policy-terms-disclaimer/',
      ariaLabel: 'Read our copyright and legal statements'
    },
    'Terms of Business': {
      url: 'https://www.mymortgageline.ca/privacy-policy-terms-disclaimer/',
      ariaLabel: 'View our terms of business and policies'
    },
    'Talk to Advisor': {
      url: 'https://www.mymortgageline.ca/contact/',
      ariaLabel: 'Schedule a consultation with a mortgage advisor'
    },
    'Apply for House Loan': {
      url: 'https://www.mymortgageline.ca/apply-online/',
      ariaLabel: 'Apply for a mortgage loan online'
    },
    'Apply for Insurance': {
      url: 'https://www.mymortgageline.ca/contact/',
      ariaLabel: 'Apply for mortgage insurance'
    },
    'User Registration': {
      url: 'https://www.mymortgageline.ca/apply-online/',
      ariaLabel: 'Register for a Mortgage Line account'
    },
    'Login': {
      url: 'https://www.mymortgageline.ca/apply-online/',
      ariaLabel: 'Login to your Mortgage Line account'
    },
    'FAQ': {
      url: 'https://www.mymortgageline.ca/resources/',
      ariaLabel: 'View frequently asked questions about mortgages'
    },
    'Our Blog': {
      url: 'https://www.mymortgageline.ca/dslc_templates/blog/',
      ariaLabel: 'Read our mortgage and real estate blog'
    }
  };

  return (
    <Box sx={{ position: 'relative', bgcolor: '#F7F9FC' }}>
      {/* Net Pattern Section */}
      <Box
        sx={{
          position: 'relative',
          height: {xs:'300px', md:'400px'},
          backgroundColor: '#F7F9FC',
          overflow: 'hidden',
          display: 'flex'
        }}
      >
        <Box
          component="img"
          src={netBg}
          alt="net"
          sx={{
            width: {xs: 'auto', md: 'auto',lg: '160%'},
            height: '100%',
            objectFit: 'fill',
            opacity: 0.6,
            mixBlendMode: 'revert'
          }}
        />
      </Box>

      {/* Main Footer Content */}
      <Box
        sx={{
          bgcolor: '#0056B3',
          color: 'white',
          pt: { xs: 8, md: 4 },
          pb: { xs: 4, md: 4 },
          position: 'relative',
          zIndex: 1,
        }}
      >
        {/* Social Media Box - Positioned above both sections */}
        <Box 
          sx={{ 
            position: 'absolute',
            top: { xs: '-35px', md: '-35px' },
            left: 0,
            right: 0,
            zIndex: 3
          }}
        >
          <SocialMediaLinks />
        </Box>

        <Container maxWidth={false} sx={{ 
            display: 'flex', 
            justifyContent: 'center',
            py: 12,
            px: { xs: 2, sm: 3, md: 4 }
          }}>
            <Box sx={{
              width: '100%',
              maxWidth: 1280,
              margin: '0 auto'
            }}>
              <Grid container spacing={4} sx={{
                justifyContent: { xs: 'center', lg: 'space-between' },
                alignItems: 'flex-start'
              }}>
                {/* About Us Column */}
                <Grid item xs={12} sm={10} md={8} lg={3}>
                  <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                    textAlign: { xs: 'center', lg: 'left' },
                    mx: 'auto',
                    maxWidth: 360
                  }}>
                    <Typography 
                      component="h2"
                      variant="h5" 
                      sx={{ 
                      fontWeight: 600, 
                      color: 'white',
                      fontSize: '1.5rem',
                      fontFamily: 'Poppins'
                      }}
                    >
                      ABOUT US
                    </Typography>
                    <Box sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2.5
                    }}>
                      {[
                        "Adipiscing consequat non tempor nec lectus. Leo nec vestibulum, gravida nunc, molestie euismod tincidunt. Maecenas odio adipiscing pharetra sit fermentum id vestibulum, laoreet.",
                        "Mi dictum vel non id cursus a tempus vel elementum."
                      ].map((text, index) => (
                        <Typography key={index} sx={{
                          color: 'white',
                          opacity: 0.8,
                          fontSize: '15px',
                          lineHeight: '26px',
                          fontWeight: 400,
                          fontFamily: 'Poppins'
                        }}>
                          {text}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                </Grid>

                {/* Link Columns */}
                <Grid item xs={12} lg={9}>
                  <Grid container spacing={{ xs: 4, lg: 2 }} sx={{
                    justifyContent: { xs: 'center', lg: 'space-between' },
                    ml: { lg: 'auto' },
                    maxWidth: { lg: 900 }
                  }}>
                    {[
                      ['IMPORTANT LINKS', ['Home', 'Our Service', 'Rates', 'Contact', 'Our Team']],
                      ['', ['Fact Find', 'Client Documentation', 'Mortgage Calculators', 'Make a Referal', 'Inquiry']],
                      ['', ['Copyright Statements', 'Terms of Business', 'Talk to Advisor', 'Apply for House Loan', 'Apply for Insurance']],
                      ['', ['User Registration', 'Login', 'FAQ', 'Our Blog']]
                    ].map(([title, items], colIndex) => (
                      <Grid item xs={6} sm={4} lg={2.8} key={colIndex} sx={{
                        minWidth: 160,
                        display: 'flex',
                        flexDirection: 'column',
                        ...(colIndex > 0 && {
                          mt: { xs: 2, sm: 3, lg: 5.5 }
                        })
                      }}>
                        <Box sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 2,
                          textAlign: { xs: 'center', lg: 'left' },
                          height: '100%',
                          mx: 'auto',
                          width: '100%'
                        }}>
                          {title && (
                            <Typography 
                              component="h3"
                              variant="h5" 
                              sx={{ 
                              fontWeight: 600, 
                              color: 'white',
                              fontSize: '1.5rem',
                              fontFamily: 'Poppins',
                              mb: 1
                              }}
                            >
                              {title}
                            </Typography>
                          )}
                          
                          <Box 
                            component="nav"
                            aria-label={title || 'Footer Links'}
                            sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1.5,
                            flex: 1,
                            ...(!title && {
                              mt: { xs: 1.5, lg: 2.5 }
                            })
                            }}
                          >
                            {items.map((item) => (
                              <Link
                                key={item}
                                href={linkMap[item]?.url || "#"}
                                aria-label={linkMap[item]?.ariaLabel}
                                sx={{
                                  color: 'white',
                                  opacity: 0.8,
                                  textDecoration: 'none',
                                  '&:hover': { 
                                    opacity: 1,
                                    textDecoration: 'underline'
                                  },
                                  fontSize: '15px',
                                  lineHeight: '26px',
                                  fontWeight: 400,
                                  fontFamily: 'Poppins',
                                  width: 'fit-content',
                                  mx: { xs: 'auto', lg: 'initial' }
                                }}
                              >
                                {item}
                              </Link>
                            ))}
                          </Box>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          </Container>
      </Box>

      {/* Copyright Text */}
      <Typography
        variant="body2"
        align="center"
        sx={{ 
          p: 2, 
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          opacity: 0.8,
          backgroundColor: (theme) => theme.palette.primary.main,
          position: 'relative',
          zIndex: 1,
          fontSize: '15px',
          lineHeight: '20px',
          fontWeight: 500,
          fontFamily: 'Poppins',
          color: 'white',
          letterSpacing: '1.5px',
          py:4,
        }}
      >
        COPYRIGHT 2025@ MORTGAGE LINE, ALL RIGHTS RESERVED.
      </Typography>
    </Box>
  );
};

export default Footer;